import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedTour } from '../i18n/localizedTours';
import SmartImage from './SmartImage';
import { getTourCardImageProps, getTourFallbackImage, getTourImage } from '../utils/images';
import { getTourPath } from '../utils/tourRoutes';
import {
  formatTourPrice,
  getGoodForTags,
  getPricePrefix,
  getPriceSuffix,
} from '../utils/tourPresentation';
import { trackTourCardClick } from '../utils/analytics';

const stars = (rating) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(5 - full - (half ? 1 : 0));
};

export default function TourCard({ tour, listName = 'Tours', itemIndex, interactive = true }) {
  const { language, translateValue } = useLanguage();
  const localizedTour = getLocalizedTour(tour, language);
  const goodForTags = getGoodForTags(localizedTour).slice(0, 3);
  const pricePrefix = translateValue('priceLabels', getPricePrefix(localizedTour.price, localizedTour));
  const priceSuffix = translateValue('priceLabels', getPriceSuffix(localizedTour.price, localizedTour));
  const tourCardImageProps = getTourCardImageProps(tour);
  const showCompareAtPrice = typeof localizedTour.compareAtPrice === 'number'
    && typeof localizedTour.price === 'number'
    && localizedTour.compareAtPrice > localizedTour.price;

  const cardContent = (
    <>
      <div className="tour-card-img-wrap">
        {localizedTour.cardBannerText && (
          <span className="tour-card-banner">{translateValue('badgeLabels', localizedTour.cardBannerText)}</span>
        )}
        <SmartImage
          src={tourCardImageProps.src || getTourImage(tour)}
          srcSet={tourCardImageProps.srcSet}
          sizes="(max-width: 767px) 92vw, (max-width: 1100px) 48vw, 338px"
          fallbackSrc={getTourFallbackImage(tour)}
          alt={localizedTour.name}
          style={tour.imagePosition ? { objectPosition: tour.imagePosition } : undefined}
        />
      </div>
      <div className="tour-card-body">
        <h3>{localizedTour.name}</h3>
        <div className="tour-rating">
          <span className="stars">{stars(localizedTour.rating)}</span>
          <span className="rating-num">{localizedTour.rating}</span>
          <span className="review-count">({localizedTour.reviews.toLocaleString()})</span>
        </div>
        {goodForTags.length > 0 && (
          <div className="tour-tags">
            {goodForTags.map((tag) => (
              <span key={tag} className="tour-tag">
                {translateValue('tagLabels', tag)}
              </span>
            ))}
          </div>
        )}
        <div className="tour-price-row">
          {showCompareAtPrice && (
            <span className="tour-price-compare" aria-label={`Original price ${formatTourPrice(localizedTour.compareAtPrice)}`}>
              {formatTourPrice(localizedTour.compareAtPrice)}
            </span>
          )}
          <span className="from">{pricePrefix}</span>
          <span className="tour-price">{formatTourPrice(localizedTour.price)}</span>
          <span className="per-person">{priceSuffix}</span>
        </div>
      </div>
    </>
  );

  if (!interactive) {
    return (
      <div className="tour-card tour-card--preview">
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      to={getTourPath(tour, language)}
      className="tour-card"
      onClick={() => trackTourCardClick({
        tour: localizedTour,
        listName,
        index: itemIndex,
      })}
    >
      {cardContent}
    </Link>
  );
}
